import { useEffect, useState } from 'react';
import { useLoaderData, useRevalidator } from 'react-router';

import { Badge, Button, Card, Icon, KpiCard } from '@ds/core';
import { DataTable } from '@ds/data';
import type { DataTableColumn } from '@ds/data';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Input, Select, Textarea } from '@ds/forms';
import { Tabs } from '@ds/navigation';
import { PageHeader } from '@/comuns/PageHeader';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import { useAlteracaoPendente } from '@/comuns/useAlteracaoPendente';
import type { CargaDoSuporte, Chamado, DadosDoChamado, ImpersonacaoRegistrada } from '@/data/tipos';
import { pode } from '@/session/sessao';

export async function carregarSuporte(): Promise<CargaDoSuporte> {
    return dados().suporte();
}

const SITUACAO: Record<Chamado['situacao'], { rotulo: string; tom: 'warning' | 'info' | 'success' }> = {
    'aberto': { rotulo: 'Aberto', tom: 'warning' },
    'em-andamento': { rotulo: 'Em andamento', tom: 'info' },
    'resolvido': { rotulo: 'Resolvido', tom: 'success' },
};

const PRIORIDADE: Record<Chamado['prioridade'], { rotulo: string; tom: 'danger' | 'neutral' }> = {
    alta: { rotulo: 'Alta', tom: 'danger' },
    media: { rotulo: 'Média', tom: 'neutral' },
    baixa: { rotulo: 'Baixa', tom: 'neutral' },
};

const CANAIS: { value: Chamado['canal']; label: string }[] = [
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'email', label: 'E-mail' },
    { value: 'telefone', label: 'Telefone' },
    { value: 'outro', label: 'Outro' },
];

/* A política é a do produto, escrita no banco (0026) e nas funções de borda
   (0040). Não é configurável — é regra inviolável sobre dado de saúde —, então a
   tela a mostra como regra, e não como interruptores que não mudariam nada. */
const POLITICA: { icone: string; texto: string }[] = [
    { icone: 'message-square-text', texto: 'Motivo obrigatório antes de entrar, com ao menos 10 caracteres.' },
    { icone: 'eye', texto: 'Somente leitura: escrever, enviar mensagem, mexer no WhatsApp ou gastar IA pela clínica é recusado pelo banco e pelas funções de borda.' },
    { icone: 'timer', texto: 'Encerra sozinha em 30 minutos.' },
    { icone: 'layers', texto: 'Uma por vez: abrir outra encerra a anterior.' },
    { icone: 'scroll-text', texto: 'Início e fim entram no log de auditoria e na ficha da clínica visitada.' },
    { icone: 'triangle-alert', texto: 'Uma faixa fixa no topo lembra o operador de que está dentro de uma clínica.' },
];

function quando(iso: string): string {
    return new Date(iso).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', dateStyle: 'short', timeStyle: 'short' });
}

type Aba = 'chamados' | 'sessoes';

export function Suporte() {
    const carga = useLoaderData() as CargaDoSuporte;
    const revalidator = useRevalidator();
    const aviso = useAviso();

    const [aba, setAba] = useState<Aba>('chamados');
    const [emEdicao, setEmEdicao] = useState<Chamado | 'novo' | null>(null);
    const [form, setForm] = useState<DadosDoChamado>({
        clinicaId: '', assunto: '', descricao: '', canal: 'whatsapp', prioridade: 'media', situacao: 'aberto',
    });
    const [salvando, setSalvando] = useState(false);
    const formSujo = useAlteracaoPendente(form, emEdicao);

    useEffect(() => {
        if (!emEdicao) return;
        if (emEdicao === 'novo') {
            setForm({
                clinicaId: carga.clinicas[0]?.id ?? '', assunto: '', descricao: '',
                canal: 'whatsapp', prioridade: 'media', situacao: 'aberto',
            });
            return;
        }
        setForm({
            clinicaId: emEdicao.clinicaId,
            assunto: emEdicao.assunto,
            descricao: emEdicao.descricao,
            canal: emEdicao.canal,
            prioridade: emEdicao.prioridade,
            situacao: emEdicao.situacao,
            ...(emEdicao.responsavelNome ? { responsavelNome: emEdicao.responsavelNome } : {}),
        });
    }, [emEdicao, carga.clinicas]);

    const salvar = async () => {
        if (!emEdicao) return;
        setSalvando(true);
        try {
            await dados().salvarChamado(emEdicao === 'novo' ? null : emEdicao.id, form);
            aviso({ title: emEdicao === 'novo' ? 'Chamado aberto' : 'Chamado salvo', description: form.assunto });
            setEmEdicao(null);
            revalidator.revalidate();
        } catch (e) {
            aviso({ title: 'Não foi possível salvar', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        } finally {
            setSalvando(false);
        }
    };

    const colunas: DataTableColumn<Chamado>[] = [
        {
            key: 'assunto', header: 'Chamado',
            render: (c) => (
                <span>
                    <strong style={{ display: 'block', fontWeight: 600 }}>{c.assunto}</strong>
                    <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                        {c.clinica} · {CANAIS.find((x) => x.value === c.canal)?.label} · aberto em {quando(c.abertoEm)}
                    </span>
                </span>
            ),
        },
        {
            key: 'prioridade', header: 'Prioridade', width: 120,
            render: (c) => <Badge tone={PRIORIDADE[c.prioridade].tom}>{PRIORIDADE[c.prioridade].rotulo}</Badge>,
        },
        {
            key: 'responsavelNome', header: 'Responsável', width: 170,
            /* Chamado sem dono é o que some: quem não é de ninguém não é
               cobrado de ninguém, e a tela precisa gritar isso. */
            render: (c) => (c.responsavelNome
                ? c.responsavelNome
                : <span style={{ color: 'var(--danger)' }}>sem responsável</span>),
        },
        {
            key: 'situacao', header: 'Situação', width: 150,
            render: (c) => (
                <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
                    <Badge tone={SITUACAO[c.situacao].tom} dot>{SITUACAO[c.situacao].rotulo}</Badge>
                    {c.horasAteResolver !== undefined && (
                        <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>em {c.horasAteResolver} h</span>
                    )}
                </span>
            ),
        },
        {
            key: 'acoes', header: '', width: 100, align: 'right',
            render: (c) => <Button size="sm" variant="secondary" onClick={() => setEmEdicao(c)}>Abrir</Button>,
        },
    ];

    const colunasDeSessao: DataTableColumn<ImpersonacaoRegistrada>[] = [
        { key: 'iniciadaEm', header: 'Início', width: 160, render: (i) => quando(i.iniciadaEm) },
        { key: 'operador', header: 'Operador', width: 180 },
        { key: 'clinica', header: 'Clínica', width: 200 },
        { key: 'motivo', header: 'Motivo' },
        {
            key: 'viva', header: 'Estado', width: 160,
            render: (i) => (
                <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
                    <Badge tone={i.viva ? 'warning' : 'neutral'} dot>{i.viva ? 'Aberta' : 'Encerrada'}</Badge>
                    <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>
                        {i.encerradaEm ? `encerrada ${quando(i.encerradaEm)}` : `expira ${quando(i.expiraEm)}`}
                    </span>
                </span>
            ),
        },
    ];

    const vivas = carga.impersonacoes.filter((i) => i.viva).length;

    return (
        <div className="vec-screen" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <PageHeader
                title="Suporte"
                description="Os chamados das clínicas e as sessões de impersonação."
                actions={aba !== 'chamados' ? undefined : pode('suporte.atender')
                    ? <Button iconLeft={<Icon name="plus" size={15} />} onClick={() => setEmEdicao('novo')}>Novo chamado</Button>
                    : (
                        <span style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}>
                            Registrar e atualizar chamado pede a permissão “Registrar e atender chamado de suporte”.
                        </span>
                    )}
            >
                <Tabs
                    ariaLabel="Seções do suporte" activeId={aba}
                    onChange={(id) => setAba(id === 'sessoes' ? 'sessoes' : 'chamados')}
                    tabs={[
                        { id: 'chamados', label: 'Chamados', count: carga.chamados.length },
                        { id: 'sessoes', label: 'Sessões de impersonação', count: carga.impersonacoes.length },
                    ]}
                />
            </PageHeader>

            {aba === 'chamados' && (
                <>
                    <div className="vec-stagger" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 12 }}>
                        {carga.indicadores.map((i) => (
                            <KpiCard key={i.id} label={i.rotulo} value={i.valor} hint={i.apoio} deltaTone={i.tendencia} icon={<Icon name={i.icone} size={16} />} />
                        ))}
                    </div>

                    {/* O chamado é o REGISTRO da conversa, não a conversa: não há caixa
                        de entrada de suporte ligada, e fingir que há faria a clínica
                        esperar resposta por um canal que ninguém lê. */}
                    <Alert tone="info" dismissible={false} title="O chamado é o registro, não o canal">
                        A clínica fala com o suporte por WhatsApp, e-mail ou telefone; quem atende anota aqui. Não há
                        caixa de entrada de suporte dentro do produto — e a clínica ainda não tem tela para abrir
                        chamado sozinha.
                    </Alert>

                    <Card padding={0}>
                        <DataTable
                            columns={colunas} rows={carga.chamados} rowKey="id"
                            stickyHeader={false} style={{ border: 0, borderRadius: 0 }}
                            emptyMessage="Nenhum chamado registrado."
                        />
                    </Card>
                </>
            )}

            {aba === 'sessoes' && (
                <>
                    <Alert tone={vivas ? 'warning' : 'neutral'} dismissible={false} title={vivas ? `${vivas} ${vivas === 1 ? 'sessão aberta agora' : 'sessões abertas agora'}` : 'Nenhuma sessão aberta agora'}>
                        Toda impersonação fica registrada, com operador, clínica, motivo, início e fim. A equipe da clínica
                        não recebe aviso em tempo real: o rastro fica no log de auditoria e na ficha dela.
                    </Alert>

                    <Card padding={0}>
                        <DataTable
                            columns={colunasDeSessao} rows={carga.impersonacoes} rowKey="id"
                            stickyHeader={false} style={{ border: 0, borderRadius: 0 }}
                            emptyMessage="Nenhuma impersonação registrada — que é o estado saudável."
                        />
                    </Card>

                    <Card title="Política de impersonação" subtitle="Regra do produto sobre dado de saúde — não é configurável" padding={18}>
                        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {POLITICA.map((p) => (
                                <li key={p.texto} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13, lineHeight: '19px' }}>
                                    <Icon name={p.icone} size={15} />
                                    <span>{p.texto}</span>
                                </li>
                            ))}
                        </ul>
                    </Card>
                </>
            )}

            <Modal
                open={emEdicao !== null}
                title={emEdicao === 'novo' ? 'Novo chamado' : 'Chamado'}
                onClose={() => setEmEdicao(null)}
                dirty={formSujo}
                width={560}
                footer={(
                    <>
                        <Button variant="ghost" onClick={() => setEmEdicao(null)}>Cancelar</Button>
                        <Button onClick={() => { void salvar(); }} disabled={!form.assunto.trim() || salvando || !pode('suporte.atender')}>
                            {salvando ? 'Salvando…' : 'Salvar'}
                        </Button>
                    </>
                )}
            >
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(200px,100%),1fr))', gap: 14 }}>
                    <FormField label="Clínica" htmlFor="chamado-clinica" required style={{ gridColumn: '1 / -1' }}>
                        <Select
                            id="chamado-clinica" value={form.clinicaId}
                            onChange={(v) => setForm({ ...form, clinicaId: v })}
                            options={carga.clinicas.map((c) => ({ value: c.id, label: c.nome }))}
                        />
                    </FormField>
                    <FormField label="Assunto" htmlFor="chamado-assunto" required style={{ gridColumn: '1 / -1' }}>
                        <Input id="chamado-assunto" value={form.assunto} onChange={(v) => setForm({ ...form, assunto: v })} />
                    </FormField>
                    <FormField label="Descrição" htmlFor="chamado-descricao" style={{ gridColumn: '1 / -1' }}>
                        <Textarea id="chamado-descricao" value={form.descricao} onChange={(v) => setForm({ ...form, descricao: v })} rows={4} />
                    </FormField>
                    <FormField label="Canal" htmlFor="chamado-canal">
                        <Select id="chamado-canal" value={form.canal} onChange={(v) => setForm({ ...form, canal: v as Chamado['canal'] })} options={CANAIS} />
                    </FormField>
                    <FormField label="Prioridade" htmlFor="chamado-prioridade">
                        <Select
                            id="chamado-prioridade" value={form.prioridade}
                            onChange={(v) => setForm({ ...form, prioridade: v as Chamado['prioridade'] })}
                            options={[{ value: 'alta', label: 'Alta' }, { value: 'media', label: 'Média' }, { value: 'baixa', label: 'Baixa' }]}
                        />
                    </FormField>
                    <FormField label="Situação" htmlFor="chamado-situacao">
                        <Select
                            id="chamado-situacao" value={form.situacao}
                            onChange={(v) => setForm({ ...form, situacao: v as Chamado['situacao'] })}
                            options={[{ value: 'aberto', label: 'Aberto' }, { value: 'em-andamento', label: 'Em andamento' }, { value: 'resolvido', label: 'Resolvido' }]}
                        />
                    </FormField>
                    <FormField label="Responsável" htmlFor="chamado-responsavel" hint="Quem, da plataforma, está com ele.">
                        <Input
                            id="chamado-responsavel" value={form.responsavelNome ?? ''}
                            onChange={(v) => setForm({ ...form, responsavelNome: v || undefined })}
                        />
                    </FormField>
                </div>
            </Modal>
        </div>
    );
}
